/**
 * Credential Manager Utility
 * Handles credential storage, expiration checking, and cleanup in sessionStorage
 */

const CREDENTIAL_STORAGE_KEY = 'dbAuth';
const DEFAULT_EXPIRATION_HOURS = 24;

/**
 * Clean up expired credentials from sessionStorage
 */
export function cleanupExpiredCredentials() {
  const stored = sessionStorage.getItem(CREDENTIAL_STORAGE_KEY);
  if (!stored) return;

  const creds = JSON.parse(stored);
  const now = new Date();
  const validCreds = creds.filter(cred => {
    if (!cred.expires_at) return true; // Keep credentials without expiration (backward compatibility)
    return new Date(cred.expires_at) > now;
  });

  if (validCreds.length !== creds.length) {
    sessionStorage.setItem(CREDENTIAL_STORAGE_KEY, JSON.stringify(validCreds));
  }
}

/**
 * Get credentials for a specific database by name
 * @param {string} name - Database name
 * @returns {Object|null} - Credential object or null if not found/expired
 */
export function getDbAuthByName(name) {
  // Clean up expired credentials first
  cleanupExpiredCredentials();
  
  const stored = sessionStorage.getItem(CREDENTIAL_STORAGE_KEY);
  if (!stored) return null;

  const creds = JSON.parse(stored);
  const cred = creds.find(item => item.name === name);
  
  if (!cred) return null;
  
  // Check if credentials are expired
  if (cred.expires_at && new Date(cred.expires_at) < new Date()) {
    // Remove expired credentials
    const updatedCreds = creds.filter(item => item.name !== name);
    sessionStorage.setItem(CREDENTIAL_STORAGE_KEY, JSON.stringify(updatedCreds));
    return null;
  }
  
  return cred;
}

/**
 * Save credentials with expiration timestamp
 * @param {string} name - Database name
 * @param {string} username - Username
 * @param {string} password - Password
 * @param {number} expirationHours - Expiration in hours (default: 24)
 */
export function saveCredentials(name, username, password, expirationHours = DEFAULT_EXPIRATION_HOURS) {
  const stored = sessionStorage.getItem(CREDENTIAL_STORAGE_KEY);
  const creds = stored ? JSON.parse(stored) : [];

  // Create expiration timestamp
  const expiresAt = new Date(Date.now() + expirationHours * 60 * 60 * 1000).toISOString();

  const updatedCreds = creds.some(item => item.name === name)
    ? creds.map(item =>
        item.name === name
          ? { name, username, password, expires_at: expiresAt }
          : item
      )
    : [...creds, { name, username, password, expires_at: expiresAt }];

  sessionStorage.setItem(CREDENTIAL_STORAGE_KEY, JSON.stringify(updatedCreds));
}

/**
 * Remove credentials for a specific database
 * @param {string} name - Database name
 */
export function removeCredentials(name) {
  const stored = sessionStorage.getItem(CREDENTIAL_STORAGE_KEY);
  if (!stored) return;

  const creds = JSON.parse(stored);
  const updatedCreds = creds.filter(item => item.name !== name);
  sessionStorage.setItem(CREDENTIAL_STORAGE_KEY, JSON.stringify(updatedCreds));
}

/**
 * Check if credentials exist and are valid for a database
 * @param {string} name - Database name
 * @returns {boolean} - True if valid credentials exist
 */
export function hasValidCredentials(name) {
  return getDbAuthByName(name) !== null;
}
